// src/services/adapters/internalPortfolio.js
// INTERNAL-COMPARABLE-ADAPTER-1 (06.10.2026)
//
// Read-only Adapter: interner Clara360-Objekt-/Mietbestand -> COMPARABLE-DATA-CONTRACT-1.
// Harte Produktregeln (Nutzervorgabe):
//  1. interne Daten = verifizierte interne Faktenquelle, NICHT automatisch Marktvergleich;
//  2. Angebots-, Kauf-, Verkaufs- und Mietpreise werden niemals vermischen;
//  3. Demo-Datensätze bleiben erhalten, sind aber aus produktiven Gates ausgeschlossen;
//  4. keine Bewertungsformel wird von diesem Adapter geändert;
//  5. Herkunftsgrade: internal_verified | internal_documented | demo_fixture.

import {
  CONTRACT_VERSION,
  normalizeComparable
} from '../comparableDataContract.js';

export const INTERNAL_ADAPTER_VERSION = 'INTERNAL-COMPARABLE-ADAPTER-1';

export const INTERNAL_PROVENANCE = {
  internal_verified: {
    id: 'internal_verified',
    label: 'Intern verifiziert',
    note: 'Kaufpreis, Kaufdatum, Adresse und Fläche vollständig aus dem eigenen Verwaltungsbestand.'
  },
  internal_documented: {
    id: 'internal_documented',
    label: 'Intern dokumentiert',
    note: 'Realer Bestandsdatensatz, aber Belegkette unvollständig (z. B. Preis ohne Kaufdatum).'
  },
  demo_fixture: {
    id: 'demo_fixture',
    label: 'Demo/Platzhalter (ausgeschlossen)',
    note: 'Test- oder Musterdaten – bleiben erhalten, zählen aber in kein produktives Gate.'
  }
};

const DEMO_CITY = /musterstadt/i;
const DEMO_NAME = /muster/i;

function has(v) {
  return v !== undefined && v !== null && v !== '';
}
function num(v) {
  const n = typeof v === 'number' ? v : parseFloat(v);
  return Number.isFinite(n) ? n : null;
}

/**
 * Herkunftsklassifikation eines internen Objektdatensatzes.
 */
export function classifyInternalProperty(row) {
  const reasons = [];
  const isDemo = DEMO_CITY.test(String(row?.city || '')) ||
    DEMO_NAME.test(String(row?.street || '')) ||
    DEMO_NAME.test(String(row?.property_name || ''));
  if (isDemo) {
    reasons.push('Demo-Muster (Ort/Straße/Name)');
    return { grade: 'demo_fixture', reasons };
  }
  const price = num(row?.purchase_price);
  const date = has(row?.purchase_date);
  const loc = has(row?.street) && has(row?.city) && has(row?.postal_code);
  const area = num(row?.total_square_meters ?? row?.rentable_area ?? row?.total_area);
  if (price && price > 0 && date && loc && area && area > 0) {
    return { grade: 'internal_verified', reasons: [] };
  }
  if (!(price && price > 0)) reasons.push('kein Kaufpreis belegt');
  if (!date) reasons.push('kein Kaufdatum belegt');
  if (!loc) reasons.push('Adresse unvollständig');
  if (!(area && area > 0)) reasons.push('keine Fläche belegt');
  return { grade: 'internal_documented', reasons };
}

/**
 * Bildet interne Objektzeilen auf das kanonische Vertragsschema ab.
 * Preistyp-Trennung: Kaufpreis nur MIT Datum als "transaction", sonst "indication".
 * Mietdaten sind NIEMAL Teil dieser Kanonisierung (separate Faktenschicht).
 */
export function adaptInternalProperties(rows = []) {
  const list = Array.isArray(rows) ? rows : [];
  return list.map((row) => {
    const { grade, reasons } = classifyInternalProperty(row);
    const purchasePrice = num(row?.purchase_price);
    const purchaseDate = has(row?.purchase_date) ? row.purchase_date : null;
    const priceType = purchasePrice && purchasePrice > 0 && purchaseDate ? 'transaction' : 'indication';
    const shaped = {
      external_id: 'properties:' + (row?.id ?? 'unbekannt'),
      source_url: 'clara360://properties/' + (row?.id ?? ''),
      price: purchasePrice && purchasePrice > 0 ? purchasePrice : num(row?.estimated_market_value),
      price_type: priceType,
      currency: 'EUR',
      living_space: num(row?.total_square_meters ?? row?.rentable_area ?? row?.total_area),
      plot_size: num(row?.ground_area),
      build_year: num(row?.construction_year ?? row?.year_built),
      property_type: row?.property_type ?? null,
      address: [row?.street, row?.postal_code, row?.city].filter(Boolean).join(', '),
      city: row?.city ?? null,
      zip_code: row?.postal_code ?? null,
      transaction_date: purchaseDate,
      observed_at: row?.updated_at ?? row?.created_at ?? purchaseDate,
      source_row: row
    };
    const canonical = normalizeComparable(shaped, 'portfolio_internal');
    return canonical ? {
      ...canonical,
      role: 'internal_fact',
      provenance_grade: grade,
      provenance_reasons: reasons,
      // Harte Produktregel: interne Fakten fließen nie in den Marktvergleich.
      market_eligible: false,
      market_exclusion_reason: 'Interne Faktenquelle – kein externes Marktdatum (INTERNAL-COMPARABLE-ADAPTER-1).',
      adapter_version: INTERNAL_ADAPTER_VERSION
    } : null;
  }).filter(Boolean);
}

/**
 * Aggregierter Bestandsstatus für die Oberfläche.
 * `usable_real_cases` ist die Entscheidungszahl für Phase 2 (Kalibrierung).
 */
export function buildInternalFactSummary(adapted = [], options = {}) {
  const subject = options.subject || null;
  const rentLayer = options.rentLayer || null;
  const by = { internal_verified: 0, internal_documented: 0, demo_fixture: 0 };
  const seen = new Set();
  let duplicates = 0;
  let local = 0;
  const subjectCity = subject?.city ? String(subject.city).toLowerCase() : null;
  adapted.forEach((c) => {
    by[c.provenance_grade] = (by[c.provenance_grade] || 0) + 1;
    const key = [c.address, c.price, c.living_space].join('|');
    if (seen.has(key)) duplicates += 1;
    else seen.add(key);
    if (subjectCity && c.city && String(c.city).toLowerCase() === subjectCity) local += 1;
  });
  return {
    contract_version: CONTRACT_VERSION,
    adapter_version: INTERNAL_ADAPTER_VERSION,
    total: adapted.length,
    internal_verified: by.internal_verified || 0,
    internal_documented: by.internal_documented || 0,
    demo_fixture_excluded: by.demo_fixture || 0,
    duplicates_excluded: duplicates,
    usable_real_cases: by.internal_verified || 0,
    local_cases: local,
    rent_layer: rentLayer,
    policy: 'Interne Faktenquelle – kein Marktvergleich. Demo-Daten ausgeschlossen. Preisarten getrennt: Kaufpreis=transaction (nur mit Datum), Schätzwert=indication, Miete=rent.'
  };
}

/**
 * Maschinenlesbare Herkunft je Zahlendarstellung der Bewertungsansicht.
 * Erfüllt den Abnahmesatz: Für jede Zahl gilt – Quelle, Datentyp, Stand, Verwendbarkeit.
 */
export function buildValueProvenanceMap(input = {}) {
  const comparablesCount = input.comparablesCount ?? 0;
  const verified = input.internalFacts?.internal_verified ?? 0;
  const now = new Date().toISOString();
  return [
    {
      id: 'ertragswert_mietbasis',
      label: 'Ertragswert – Mietbasis',
      source_name: 'Interner Mietbestand (Clara360)',
      source_class: 'portfolio_internal',
      price_type: 'rent',
      observed_at: now,
      allowed: true,
      allowed_use: 'Interne Faktenschicht für den Ertragswert – keine Marktpreise.',
      contract_version: CONTRACT_VERSION
    },
    {
      id: 'kaufpreis_referenz',
      label: 'Kaufpreis-Referenzen (eigener Bestand)',
      source_name: 'Interner Objektbestand (Clara360), ' + verified + ' verifizierte Fälle',
      source_class: 'portfolio_internal',
      price_type: 'transaction',
      observed_at: now,
      allowed: true,
      allowed_use: 'Interne Fakten – ausdrücklich kein Marktvergleich.',
      contract_version: CONTRACT_VERSION
    },
    {
      id: 'angebotsdaten_altbestand',
      label: 'Angebotsdaten Altbestand (' + comparablesCount + ' Sätze)',
      source_name: 'property_suggestions (Demo/Inserats-Snapshots)',
      source_class: 'legacy_seed',
      price_type: 'asking',
      observed_at: null,
      allowed: false,
      allowed_use: 'Ausgeschlossen – Demo/Altbestand ohne Herkunftsnachweis.',
      contract_version: CONTRACT_VERSION
    },
    {
      id: 'bodenrichtwert',
      label: 'Bodenrichtwert',
      source_name: 'Regional abgeleitet (keine amtliche BORIS-Abfrage)',
      source_class: 'unknown',
      price_type: 'land_value',
      observed_at: null,
      allowed: false,
      allowed_use: 'Nur Orientierung – kein amtlicher Bodenrichtwert, kein Vergleichspreis.',
      contract_version: CONTRACT_VERSION
    },
    {
      id: 'vergleichswert',
      label: 'Vergleichswert',
      source_name: 'Marktvergleich (Freigabegates)',
      source_class: 'transaction_official',
      price_type: 'transaction',
      observed_at: null,
      allowed: false,
      allowed_use: 'Gesperrt, bis die Freigabekriterien Marktvergleich erfüllt sind.',
      contract_version: CONTRACT_VERSION
    }
  ];
}
